import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { DateTime } from "luxon";
import BackToDashboard from "@/components/BackToDashboard";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, Check, Tag, Lock, ArrowUpRight, Plus, Trash2, X, Info, Clock, Timer, Pencil, Users, Package, Globe } from "lucide-react";
import { CURRENCY_SYMBOLS, currencySymbol } from "@/lib/currency";

/**
 * ShippingInfo.jsx
 * ------------------------------------------------------------------
 * Shipping fee — available on every plan.
 * - Loads real data from GET /shipping on mount, saves via POST /shipping.
 * Discount rules (Growth/Pro only) live here too, including optional
 * "limited-time" start/end windows. Merchants only ever see and enter times
 * in their own account timezone (from GET /timezone) — everything is
 * converted to/from UTC before it touches the API, since discount_rules
 * stores starts_at/expires_at as UTC timestamps.
 * ------------------------------------------------------------------
 */

// -------------------- Timezone helpers --------------------------------
// Fetches the merchant's stored account timezone once. This is the zone set
// during onboarding (or by CheckoutDM Support) — never the browser's current
// zone, so a merchant traveling doesn't see their own offers shift on them.
function useBusinessTimezone() {
  const [timezone, setTimezone] = useState(null);
  const [timezoneLabel, setTimezoneLabel] = useState(null);

  useEffect(() => {
    api
      .get("/timezone")
      .then(({ data }) => {
        setTimezone(data.timezone || "Asia/Kolkata");
        setTimezoneLabel(data.timezone_label || data.timezone || "Asia/Kolkata");
      })
       .catch(() => {
        // Non-critical — fall back to the browser's own timezone so a
        // merchant outside India doesn't silently see IST-labeled times.
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        setTimezone(browserTz);
        setTimezoneLabel(browserTz.replace(/_/g, " "));
      });
  }, []);

  return { timezone, timezoneLabel };
}

// A <input type="datetime-local"> value ("2026-09-14T12:00") has no timezone
// of its own — it's just wall-clock numbers. We tell Luxon those numbers are
// wall-clock time *in the merchant's account timezone*, then convert to UTC.
function localInputToUtcIso(value, timezone) {
  if (!value || !timezone) return null;
  const dt = DateTime.fromISO(value, { zone: timezone });
  return dt.isValid ? dt.toUTC().toISO() : null;
}

// Reverse: take a UTC ISO string from the API and format it for display, or
// for pre-filling a datetime-local input, in the merchant's account timezone.
function utcIsoToLocalDisplay(value, timezone) {
  if (!value || !timezone) return null;
  const dt = DateTime.fromISO(value, { zone: "utc" }).setZone(timezone);
  return dt.isValid ? dt : null;
}

// Same conversion, but formatted for pre-filling a datetime-local input.
function utcIsoToLocalInputValue(value, timezone) {
  const dt = utcIsoToLocalDisplay(value, timezone);
  return dt ? dt.toFormat("yyyy-MM-dd'T'HH:mm") : "";
}

export default function ShippingInfo() {
    const [shippingFee, setShippingFee] = useState("");
  const [freeShippingThreshold, setFreeShippingThreshold] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [plan, setPlan] = useState("free");
  const [error, setError] = useState("");
  const [thresholdError, setThresholdError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/shipping")
      .then(({ data }) => {
        setShippingFee(data.shipping_fee != null ? String(data.shipping_fee) : "0");
        setFreeShippingThreshold(
          data.free_shipping_min_cart_value != null ? String(data.free_shipping_min_cart_value) : ""
        );
        setCurrency(data.currency || "INR");
        setPlan(data.plan || "free");
      })
      .catch(() => toast.error("Couldn't load your shipping settings. Try refreshing."))
      .finally(() => setLoading(false));
  }, []);

  const symbol = currencySymbol(currency);

  const set = (v) => {
    setShippingFee(v.replace(/[^\d.]/g, ""));
    if (error) setError("");
    if (saved) setSaved(false);
  };

  const setThreshold = (v) => {
    setFreeShippingThreshold(v.replace(/[^\d.]/g, ""));
    if (thresholdError) setThresholdError("");
    if (saved) setSaved(false);
  };

  const submit = async (evt) => {
    evt.preventDefault();
    const fee = Number(shippingFee);
    if (shippingFee === "" || !Number.isFinite(fee) || fee < 0 || fee > 100000) {
      setError("Enter a shipping fee between 0 and 100,000.");
      toast.error("Please fix the shipping fee before saving.");
      return;
    }
    let threshold = null;
    if (freeShippingThreshold !== "") {
      threshold = Number(freeShippingThreshold);
      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1000000) {
        setThresholdError("Enter a value between 0 and 1,000,000, or leave it blank to turn this off.");
        toast.error("Please fix the free shipping threshold before saving.");
        return;
      }
    }
    setSaving(true);
    try {
      await api.post("/shipping", { shipping_fee: fee, free_shipping_min_cart_value: threshold });
      setSaved(true);
      toast.success("Shipping fee saved.");
      setTimeout(() => setSaved(false), 2600);
    } catch {
      toast.error("Couldn't save your shipping fee. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
        <div className="mx-auto max-w-4xl">
          <BackToDashboard />
          <div className="mt-10 grid h-40 place-items-center text-sm text-ink/50">Loading…</div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-4xl">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">
          Shipping & discounts
        </p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-ink md:text-6xl">
          Set your delivery fee, run offers.
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Your shipping fee is added to every order at checkout. Discount codes and automatic
          offers live here too.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          <Section
            title="Shipping fee"
            caption="A flat delivery charge added to every order's total. Set it to 0 for free shipping on everything."
          >
            <Field label="Flat shipping fee" hint={`in ${currency}`}>
              <div className="relative rounded-2xl border border-ink/15 bg-white transition-colors focus-within:border-hotpink">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink/40">
                  {symbol}
                </span>
                <input
                  inputMode="decimal"
                  value={shippingFee}
                  onChange={(e) => set(e.target.value)}
                  placeholder="49"
                  className="w-40 bg-transparent py-2.5 pl-8 pr-3 text-sm outline-none"
                  data-testid="shipping-fee-input"
                />
              </div>
              {error && <span className="mt-1 block text-xs font-semibold text-hotpink">{error}</span>}
            </Field>
                    </Section>

          {plan === "free" ? (
            <Section
              title="Conditional free shipping"
              caption="Available on Starter, Growth & Pro — waive the shipping fee automatically once a customer's cart crosses a value you choose."
            >
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-5">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                    <Lock size={18} />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Starter, Growth & Pro</p>
                    <p className="mt-0.5 text-sm text-ink/70">Upgrade to give customers a reason to buy more instead of paying for delivery.</p>
                  </div>
                </div>
                <a
                  href="/plans"
                  className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
                >
                  Upgrade <ArrowUpRight size={14} />
                </a>
              </div>
            </Section>
          ) : (
            <Section
              title="Conditional free shipping"
              caption="Waive the shipping fee automatically once a customer's cart crosses a value you choose — gives customers a reason to buy more instead of paying for delivery. Applies to every order, no limit."
            >
              <Field label="Free shipping over" hint={`in ${currency} · leave blank to turn off`}>
                <div className="relative w-40 rounded-2xl border border-ink/15 bg-white transition-colors focus-within:border-hotpink">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink/40">
                    {symbol}
                  </span>
                  <input
                    inputMode="decimal"
                    value={freeShippingThreshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    placeholder="e.g. 999"
                    className="w-full bg-transparent py-2.5 pl-8 pr-3 text-sm outline-none"
                    data-testid="free-shipping-threshold-input"
                  />
                </div>
                {thresholdError && (
                  <span className="mt-1 block text-xs font-semibold text-hotpink">{thresholdError}</span>
                )}
              </Field>
            </Section>
          )}

          <div className="sticky bottom-4 z-30 flex flex-col items-stretch gap-3 rounded-3xl border border-ink/10 bg-white/90 px-5 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-1.5 text-xs text-ink/60">
              <Truck size={14} className="text-ink/40" /> Shipping fee applies to every order, regardless of plan.
            </p>
            <div className="flex items-center gap-2">
              <AnimatePresence>
                {saved && (
                  <motion.span
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    className="inline-flex items-center gap-1 rounded-full bg-mint px-3 py-1 text-xs font-semibold text-ink"
                    data-testid="shipping-saved"
                  >
                    <Check className="h-3.5 w-3.5" /> Saved
                  </motion.span>
                )}
              </AnimatePresence>
              <button
                type="submit"
                disabled={saving}
                data-testid="shipping-save-btn"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-60 sm:w-auto"
              >
                {saving ? "Saving…" : "Save shipping fee"}
              </button>
            </div>
          </div>
        </form>

        <div className="mt-8">
          <DiscountRulesSection plan={plan} currency={currency} />
        </div>
      </div>
    </section>
  );
}

// -------------------- Discount rules section --------------------------------
function DiscountRulesSection({ plan, currency }) {
  const unlocked = plan === "growth" || plan === "pro";
  const exampleSymbol = currencySymbol(currency) || "$";
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(unlocked);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { timezone, timezoneLabel } = useBusinessTimezone();

  const load = () => {
    if (!unlocked) return;
    setLoading(true);
    api
      .get("/discounts")
      .then(({ data }) => setRules(data.rules || []))
      .catch(() => toast.error("Couldn't load your discount rules."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [unlocked]);

  if (!unlocked) {
    return (
      <Section
        title="Discount codes & automatic offers"
        caption={`Available on Growth and Pro — flat/percent codes, plus automatic offers like 'free shipping over ${exampleSymbol}500'.`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-5">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <Lock size={18} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Growth & Pro</p>
              <p className="mt-0.5 text-sm text-ink/70">Upgrade to create discount codes and automatic offers.</p>
            </div>
          </div>
          <a
            href="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
          >
            Upgrade <ArrowUpRight size={14} />
          </a>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Discount codes & automatic offers"
      caption={`Manual codes customers type in DM, or automatic offers applied when an order crosses a threshold — e.g. free shipping over ${exampleSymbol}500.`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
          {loading
            ? "Loading…"
            : `${rules.length} rule${rules.length === 1 ? "" : "s"} · ${rules.filter((r) => r.show_on_public_catalog).length}/3 featured on public catalog`}
        </p>
        <button
          type="button"
          onClick={() => {
            setEditingId(null);
            setFormOpen((o) => !o);
          }}
          data-testid="discount-new-rule-btn"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
            formOpen
              ? "border border-ink/15 bg-white text-ink hover:bg-ink/5"
              : "cta-glow bg-sunset-gradient text-white"
          }`}
        >
          {formOpen ? <X size={14} /> : <Plus size={14} />} {formOpen ? "Cancel" : "New rule"}
        </button>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-ink/10 bg-ink/[0.02] p-4">
        <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-ink/5 text-ink/50">
          <Info size={14} />
        </span>
        <p className="text-xs leading-relaxed text-ink/60">
          Only <strong className="text-ink">one</strong> discount applies per order, and{" "}
          <strong className="text-ink">automatic offers always win</strong> over manual codes.{" "}
          <Link to="/discounts-guide" className="font-semibold text-hotpink hover:underline">
            See how priority, limited-time windows & reuse work →
          </Link>
        </p>
      </div>

      {timezoneLabel && (
        <p className="flex items-start gap-1.5 text-xs text-ink/50">
          <Clock size={12} className="mt-0.5 flex-none" />
          <span>
            All times below are shown in your account's timezone —{" "}
            <span className="font-semibold text-ink/70">{timezoneLabel}</span>.
          </span>
        </p>
      )}

      <AnimatePresence>
        {formOpen && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <CreateRuleForm
              currency={currency}
              timezone={timezone}
              plan={plan}
              onCreated={() => {
                setFormOpen(false);
                load();
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {!loading && rules.length === 0 && !formOpen && (
        <p className="rounded-2xl border border-dashed border-ink/15 bg-ink/[0.02] p-5 text-sm text-ink/50">
          No discount rules yet — create one to offer codes or automatic order-value discounts.
        </p>
      )}

      {rules.length > 0 && (
        <div className="space-y-2">
                    {rules.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              currency={currency}
              timezone={timezone}
              plan={plan}
              onChanged={load}
              isEditing={editingId === r.id}
              onStartEdit={() => {
                setFormOpen(false);
                setEditingId(r.id);
              }}
              onCancelEdit={() => setEditingId(null)}
              onSaved={() => {
                setEditingId(null);
                load();
              }}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function describeRule(r, currency) {
  const symbol = currencySymbol(currency);
  const value =
    r.discount_kind === "percent"
      ? `${Number(r.discount_value)}% off`
      : r.discount_kind === "flat"
      ? `${symbol}${Number(r.discount_value)} off`
      : "Free shipping";
  const min = r.min_order_value != null ? ` on orders over ${symbol}${Number(r.min_order_value)}` : "";
  const qty = r.min_quantity != null ? ` (cart has ${r.min_quantity}+ items)` : "";
  return `${value}${min}${qty}`;
}

// Renders the optional starts_at / expires_at window for a rule, converted
// into the merchant's account timezone. Returns null if the rule has no
// time window set at all (the common case).
function TimeWindowBadge({ rule, timezone }) {
  if (!rule.starts_at && !rule.expires_at) return null;
  if (!timezone) return null;

  const now = DateTime.now().setZone(timezone);
  const starts = utcIsoToLocalDisplay(rule.starts_at, timezone);
  const expires = utcIsoToLocalDisplay(rule.expires_at, timezone);

  const notStartedYet = starts && starts > now;
  const isExpired = expires && expires <= now;

  const fmt = (dt) => dt.toFormat("ccc, d LLL · h:mm a");

  let label;
  let tone = "bg-lilac text-ink";
  if (isExpired) {
    label = `Expired ${fmt(expires)}`;
    tone = "bg-ink/10 text-ink/50";
  } else if (notStartedYet) {
    label = `Starts ${fmt(starts)}`;
    tone = "bg-peach text-ink";
  } else if (expires) {
    label = `Ends ${fmt(expires)}`;
    tone = "bg-mint text-ink";
  } else if (starts) {
    label = `Started ${fmt(starts)}`;
  }

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
      <Timer size={11} /> {label}
    </span>
  );
}

// Shows the "min quantity" condition on a rule, e.g. "2+ items". Only rules
// created with a quantity threshold have this — most rules won't.
function QuantityBadge({ rule }) {
  if (rule.min_quantity == null) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-peach px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
      <Package size={11} /> {rule.min_quantity}+ items
    </span>
  );
}

// Shows how many times a rule has been used, and its limit if one was set.
// Stays hidden for rules that have never been used and have no limit — no
// need to clutter the row with "0 used" for every ordinary coupon.
function UsesBadge({ rule }) {
  const used = rule.uses_count ?? 0;
  const max = rule.max_uses_total;
  if (!used && max == null) return null;

  const label = max != null ? `${used} / ${max} used` : `${used} used`;
  const atLimit = max != null && used >= max;
  const tone = atLimit ? "bg-hotpink/10 text-hotpink" : "bg-ink/5 text-ink/60";

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
      <Users size={11} /> {label}
    </span>
  );
}

function FeaturedBadge({ rule }) {
  if (!rule.show_on_public_catalog) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-lilac px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
      <Globe size={11} /> On public page
    </span>
  );
}

function RuleRow({ rule, currency, timezone, plan, onChanged, isEditing, onStartEdit, onCancelEdit, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isEditing) {
    return (
      <div
        className="rounded-2xl border border-hotpink/30 bg-white p-4"
        data-testid={`discount-rule-edit-${rule.id}`}
      >
        <EditRuleForm rule={rule} currency={currency} timezone={timezone} plan={plan} onCancel={onCancelEdit} onSaved={onSaved} />
      </div>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      await api.post("/discounts/toggle", { id: rule.id, active: !rule.active });
      toast.success(rule.active ? "Rule deactivated." : "Rule activated.");
      onChanged();
    } catch {
      toast.error("Couldn't update that rule.");
    } finally {
      setBusy(false);
    }
  };

  const togglePublicCatalog = async () => {
    setBusy(true);
    try {
      await api.post("/discounts/toggle", { id: rule.id, show_on_public_catalog: !rule.show_on_public_catalog });
      toast.success(rule.show_on_public_catalog ? "Removed from public page." : "Now showing on your public page.");
      onChanged();
    } catch (e) {
      const code = e?.response?.data?.error;
      toast.error(
        code === "public_catalog_limit_reached"
          ? "You can feature up to 3 coupons at a time. Turn one off first."
          : "Couldn't update that rule."
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/discounts?id=${rule.id}`);
      toast.success("Rule deleted.");
      onChanged();
    } catch (e) {
      const code = e?.response?.data?.error;
      toast.error(
        code === "rule_in_use_deactivate_instead"
          ? "This rule has already been used — deactivate it instead of deleting."
          : "Couldn't delete that rule."
      );
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink/10 bg-white p-4"
      data-testid={`discount-rule-${rule.id}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${rule.active ? "bg-mint text-ink" : "bg-ink/5 text-ink/40"}`}>
          <Tag size={15} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-ink">
            {rule.rule_type === "manual_code" ? rule.code : "Automatic offer"}
          </p>
          <p className="truncate text-xs text-ink/50">{describeRule(rule, currency)}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <TimeWindowBadge rule={rule} timezone={timezone} />
            <QuantityBadge rule={rule} />
            <UsesBadge rule={rule} />
            <FeaturedBadge rule={rule} />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 sm:items-end">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${rule.active ? "bg-mint/60 text-ink" : "bg-ink/5 text-ink/40"}`}>
            {rule.active ? "Active" : "Inactive"}
          </span>
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className="rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5 disabled:opacity-40"
          >
            {rule.active ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button"
            onClick={onStartEdit}
            disabled={busy}
            title="Edit"
            className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 bg-white text-ink/60 hover:bg-ink/5 disabled:opacity-40"
            data-testid={`discount-edit-btn-${rule.id}`}
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            title="Delete"
            className="grid h-8 w-8 place-items-center rounded-full border border-ink/15 bg-white text-hotpink hover:bg-hotpink/5 disabled:opacity-40"
          >
            <Trash2 size={13} />
          </button>
        </div>
        <button
          type="button"
          onClick={togglePublicCatalog}
          disabled={busy}
          title={rule.show_on_public_catalog ? "Remove from public catalog page" : "Show on public catalog page"}
          data-testid={`discount-public-toggle-${rule.id}`}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${
            rule.show_on_public_catalog
              ? "border-hotpink/30 bg-hotpink/5 text-hotpink hover:bg-hotpink/10"
              : "border-ink/15 bg-white hover:bg-ink/5"
          }`}
        >
          <Globe size={13} /> {rule.show_on_public_catalog ? "On public page" : "Show on public page"}
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6">
            <p className="font-display text-xl font-black tracking-tight">Delete this rule?</p>
            <p className="mt-2 text-sm text-ink/60">
              {rule.rule_type === "manual_code" ? `Code "${rule.code}"` : "This automatic offer"} will be permanently removed. This can't be undone.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={remove}
                disabled={busy}
                className="flex-1 rounded-full bg-hotpink py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Quick presets for the "Ends" field. Each returns a datetime-local input
// value ("YYYY-MM-DDTHH:mm") computed as wall-clock time in the merchant's
// account timezone — NOT the browser's current timezone, which matters if
// they're checking the dashboard while traveling.
function buildEndPresets(timezone) {
  if (!timezone) return [];
  const now = DateTime.now().setZone(timezone);
  const toValue = (dt) => dt.toFormat("yyyy-MM-dd'T'HH:mm");

  return [
    { label: "Tonight, Midnight", value: toValue(now.set({ hour: 23, minute: 59, second: 59, millisecond: 0 })) },
    { label: "This Sunday, noon", value: toValue(now.set({ hour: 12, minute: 0 }).plus({ days: (7 - now.weekday) % 7 || 7 })) },
    { label: "In 24 hours", value: toValue(now.plus({ hours: 24 })) },
    { label: "In 3 days", value: toValue(now.plus({ days: 3 })) },
  ];
}

function CreateRuleForm({ currency, timezone, plan, onCreated }) {
  const [ruleType, setRuleType] = useState("manual_code");
  const [code, setCode] = useState("");
  const [discountKind, setDiscountKind] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [minOrderValue, setMinOrderValue] = useState("");
  const [minQuantity, setMinQuantity] = useState("");
  const [maxUsesTotal, setMaxUsesTotal] = useState("");
  const [saving, setSaving] = useState(false);

  const [timeWindowOpen, setTimeWindowOpen] = useState(false);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [expiresAtLocal, setExpiresAtLocal] = useState("");

  const presets = buildEndPresets(timezone);

  const submit = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (ruleType === "manual_code" && !code.trim()) {
      toast.error("Enter a code for this rule.");
      return;
    }
    if (discountKind !== "free_shipping" && (!discountValue || Number(discountValue) <= 0)) {
      toast.error("Enter a discount value.");
      return;
    }

    let starts_at = null;
    let expires_at = null;
    if (timeWindowOpen) {
      if (!timezone) {
        toast.error("Still loading your account timezone — try again in a moment.");
        return;
      }
      starts_at = localInputToUtcIso(startsAtLocal, timezone);
      expires_at = localInputToUtcIso(expiresAtLocal, timezone);
      if (startsAtLocal && !starts_at) {
        toast.error("That start date/time doesn't look valid.");
        return;
      }
      if (expiresAtLocal && !expires_at) {
        toast.error("That end date/time doesn't look valid.");
        return;
      }
      if (starts_at && expires_at && new Date(expires_at) <= new Date(starts_at)) {
        toast.error("The end time has to be after the start time.");
        return;
      }
    }

    setSaving(true);
    try {
        await api.post("/discounts", {
        rule_type: ruleType,
        code: ruleType === "manual_code" ? code.trim() : "",
        discount_kind: discountKind,
        discount_value: discountKind === "free_shipping" ? null : Number(discountValue),
        min_order_value: minOrderValue === "" ? null : Number(minOrderValue),
        min_quantity: minQuantity === "" ? null : Number(minQuantity),
        max_uses_total: maxUsesTotal === "" ? null : Number(maxUsesTotal),
        starts_at,
        expires_at,
      });
      toast.success("Discount rule created.");
      onCreated();
    } catch (e) {
      const errCode = e?.response?.data?.error;
      toast.error(
        errCode === "code_already_exists_or_save_failed"
          ? "That code is already in use."
          : errCode === "expires_before_starts"
          ? "The end time has to be after the start time."
          : "Couldn't create that rule — check the values and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5" onClick={(e) => e.stopPropagation()}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Type">
          <select
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value)}
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-rule-type"
          >
            <option value="manual_code">Manual code — customer types it</option>
            <option value="automatic">Automatic — applies with no code</option>
          </select>
        </Field>

        {ruleType === "manual_code" && (
          <Field label="Code" hint="e.g. WELCOME10">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="WELCOME10"
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-code-input"
            />
          </Field>
        )}

        <Field label="Discount type">
          <select
            value={discountKind}
            onChange={(e) => setDiscountKind(e.target.value)}
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-kind"
          >
            <option value="percent">Percent off</option>
            <option value="flat">Flat amount off</option>
            <option value="free_shipping">Free shipping</option>
          </select>
        </Field>

        {discountKind !== "free_shipping" && (
          <Field label={discountKind === "percent" ? "Percent off" : `Amount off (${currency})`}>
            <input
              inputMode="decimal"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder={discountKind === "percent" ? "10" : "100"}
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-value-input"
            />
          </Field>
        )}

        <Field label="Minimum order value" hint="optional">
          <input
            inputMode="decimal"
            value={minOrderValue}
            onChange={(e) => setMinOrderValue(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="e.g. 500"
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-min-order-input"
          />
        </Field>

        {plan === "pro" && (
          <Field label="Minimum cart quantity" hint="optional — e.g. 2 for 'Buy 2 Get X% Off'">
            <input
              inputMode="numeric"
              value={minQuantity}
              onChange={(e) => setMinQuantity(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="e.g. 2"
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-min-quantity-input"
            />
          </Field>
        )}

        <Field label="Total use limit" hint="optional — e.g. first 100 customers">
          <input
            inputMode="numeric"
            value={maxUsesTotal}
            onChange={(e) => setMaxUsesTotal(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="e.g. 100"
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-max-uses-input"
          />
        </Field>
      </div>

      <div className="mt-4 border-t border-ink/10 pt-4">
        <button
          type="button"
          onClick={() => setTimeWindowOpen((o) => !o)}
          data-testid="discount-time-window-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
            timeWindowOpen
              ? "border-hotpink bg-hotpink text-white hover:opacity-90"
              : "border-hotpink/30 bg-hotpink/5 text-hotpink hover:bg-hotpink/10"
          }`}
        >
          <Timer size={13} /> {timeWindowOpen ? "Remove limited-time window" : "Make this a limited-time offer"}
        </button>

        {timeWindowOpen && (
          <div className="mt-3 space-y-3 rounded-2xl border border-ink/10 bg-white p-4">
            <p className="text-xs text-ink/50">
              Leave "Starts" blank to activate immediately. Leave "Ends" blank for no expiry. Once
              "Ends" passes, the offer stops applying automatically — even for customers who never used it.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Starts" hint="optional">
                <input
                  type="datetime-local"
                  value={startsAtLocal}
                  onChange={(e) => setStartsAtLocal(e.target.value)}
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="discount-starts-at-input"
                />
              </Field>
              <Field label="Ends" hint="optional">
                <input
                  type="datetime-local"
                  value={expiresAtLocal}
                  onChange={(e) => setExpiresAtLocal(e.target.value)}
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="discount-expires-at-input"
                />
              </Field>
            </div>

            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setExpiresAtLocal(p.value)}
                    className="rounded-full border border-ink/15 bg-ink/[0.02] px-3 py-1.5 text-xs font-semibold text-ink/70 hover:bg-ink/5"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={saving}
        data-testid="discount-create-submit"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
      >
        {saving ? "Creating…" : "Create rule"}
      </button>
    </div>
  );
}

function EditRuleForm({ rule, currency, timezone, plan, onCancel, onSaved }) {
  const isManualCode = rule.rule_type === "manual_code";

  const [code, setCode] = useState(rule.code || "");
  const [discountKind, setDiscountKind] = useState(rule.discount_kind);
  const [discountValue, setDiscountValue] = useState(
    rule.discount_value != null ? String(rule.discount_value) : ""
  );
  const [minOrderValue, setMinOrderValue] = useState(
    rule.min_order_value != null ? String(rule.min_order_value) : ""
  );
  const [minQuantity, setMinQuantity] = useState(
    rule.min_quantity != null ? String(rule.min_quantity) : ""
  );
  const [maxUsesTotal, setMaxUsesTotal] = useState(
    rule.max_uses_total != null ? String(rule.max_uses_total) : ""
  );
  const [saving, setSaving] = useState(false);

  const [timeWindowOpen, setTimeWindowOpen] = useState(Boolean(rule.starts_at || rule.expires_at));
  const [startsAtLocal, setStartsAtLocal] = useState(utcIsoToLocalInputValue(rule.starts_at, timezone));
  const [expiresAtLocal, setExpiresAtLocal] = useState(utcIsoToLocalInputValue(rule.expires_at, timezone));

  const presets = buildEndPresets(timezone);

  const submit = async (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (isManualCode && !code.trim()) {
      toast.error("Enter a code for this rule.");
      return;
    }
    if (discountKind !== "free_shipping" && (!discountValue || Number(discountValue) <= 0)) {
      toast.error("Enter a discount value.");
      return;
    }

    let starts_at = null;
    let expires_at = null;
    if (timeWindowOpen) {
      if (!timezone) {
        toast.error("Still loading your account timezone — try again in a moment.");
        return;
      }
      starts_at = localInputToUtcIso(startsAtLocal, timezone);
      expires_at = localInputToUtcIso(expiresAtLocal, timezone);
      if (startsAtLocal && !starts_at) {
        toast.error("That start date/time doesn't look valid.");
        return;
      }
      if (expiresAtLocal && !expires_at) {
        toast.error("That end date/time doesn't look valid.");
        return;
      }
      if (starts_at && expires_at && new Date(expires_at) <= new Date(starts_at)) {
        toast.error("The end time has to be after the start time.");
        return;
      }
    }

    setSaving(true);
    try {
      await api.patch("/discounts", {
        id: rule.id,
        code: isManualCode ? code.trim() : "",
        discount_kind: discountKind,
        discount_value: discountKind === "free_shipping" ? null : Number(discountValue),
        min_order_value: minOrderValue === "" ? null : Number(minOrderValue),
        min_quantity: plan === "pro" ? (minQuantity === "" ? null : Number(minQuantity)) : null,
        max_uses_total: maxUsesTotal === "" ? null : Number(maxUsesTotal),
        starts_at,
        expires_at,
      });
      toast.success("Discount rule updated.");
      onSaved();
    } catch (e) {
      const errCode = e?.response?.data?.error;
      toast.error(
        errCode === "code_already_exists_or_save_failed"
          ? "That code is already in use."
          : errCode === "expires_before_starts"
          ? "The end time has to be after the start time."
          : errCode === "automatic_rules_cannot_have_a_code"
          ? "Automatic offers can't have a code."
          : errCode === "owner_only"
          ? "Only the account owner can edit discount rules."
          : errCode === "rule_not_found"
          ? "This rule no longer exists."
          : "Couldn't update that rule — check the values and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-hotpink">
        <Pencil size={12} /> Editing {isManualCode ? "code" : "automatic offer"}
      </div>

      {plan !== "pro" && rule.min_quantity != null && (
        <div className="mb-4 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-3 text-xs leading-relaxed text-ink/70">
          This rule has a minimum quantity condition, which is a Pro feature. Since this account is no longer on Pro, saving will remove that condition — the rest of the rule stays active as-is.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {isManualCode && (
          <Field label="Code" hint="e.g. WELCOME10">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="WELCOME10"
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-edit-code-input"
            />
          </Field>
        )}

        <Field label="Discount type">
          <select
            value={discountKind}
            onChange={(e) => setDiscountKind(e.target.value)}
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-edit-kind"
          >
            <option value="percent">Percent off</option>
            <option value="flat">Flat amount off</option>
            <option value="free_shipping">Free shipping</option>
          </select>
        </Field>

        {discountKind !== "free_shipping" && (
          <Field label={discountKind === "percent" ? "Percent off" : `Amount off (${currency})`}>
            <input
              inputMode="decimal"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder={discountKind === "percent" ? "10" : "100"}
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-edit-value-input"
            />
          </Field>
        )}

        <Field label="Minimum order value" hint="optional">
          <input
            inputMode="decimal"
            value={minOrderValue}
            onChange={(e) => setMinOrderValue(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="e.g. 500"
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-edit-min-order-input"
          />
        </Field>

        {plan === "pro" && (
          <Field label="Minimum cart quantity" hint="optional — e.g. 2 for 'Buy 2 Get X% Off'">
            <input
              inputMode="numeric"
              value={minQuantity}
              onChange={(e) => setMinQuantity(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="e.g. 2"
              className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
              data-testid="discount-edit-min-quantity-input"
            />
          </Field>
        )}

        <Field label="Total use limit" hint="optional — across all customers">
          <input
            inputMode="numeric"
            value={maxUsesTotal}
            onChange={(e) => setMaxUsesTotal(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="e.g. 100"
            className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
            data-testid="discount-edit-max-uses-input"
          />
        </Field>
      </div>

      <div className="mt-4 border-t border-ink/10 pt-4">
        <button
          type="button"
          onClick={() => setTimeWindowOpen((o) => !o)}
          data-testid="discount-edit-time-window-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
            timeWindowOpen
              ? "border-hotpink bg-hotpink text-white hover:opacity-90"
              : "border-hotpink/30 bg-hotpink/5 text-hotpink hover:bg-hotpink/10"
          }`}
        >
          <Timer size={13} /> {timeWindowOpen ? "Remove limited-time window" : "Make this a limited-time offer"}
        </button>

        {timeWindowOpen && (
          <div className="mt-3 space-y-3 rounded-2xl border border-ink/10 bg-white p-4">
            <p className="text-xs text-ink/50">
              Leave "Starts" blank to activate immediately. Leave "Ends" blank for no expiry.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Starts" hint="optional">
                <input
                  type="datetime-local"
                  value={startsAtLocal}
                  onChange={(e) => setStartsAtLocal(e.target.value)}
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="discount-edit-starts-at-input"
                />
              </Field>
              <Field label="Ends" hint="optional">
                <input
                  type="datetime-local"
                  value={expiresAtLocal}
                  onChange={(e) => setExpiresAtLocal(e.target.value)}
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  data-testid="discount-edit-expires-at-input"
                />
              </Field>
            </div>

            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setExpiresAtLocal(p.value)}
                    className="rounded-full border border-ink/15 bg-ink/[0.02] px-3 py-1.5 text-xs font-semibold text-ink/70 hover:bg-ink/5"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="discount-edit-submit"
          className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-full border border-ink/15 bg-white px-5 py-2 text-sm font-semibold hover:bg-ink/5 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// -------------------- Reusable subcomponents --------------------------------
function Section({ title, caption, children }) {
  return (
    <div className="rounded-3xl border border-ink/10 bg-white p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-ink">{title}</h2>
        {caption && <p className="mt-1 whitespace-pre-line text-sm text-ink/50">{caption}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-ink/70">
        <span>{label}</span>
        {hint && <span className="text-[10px] font-medium normal-case text-ink/40">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
